import { useEffect, useState } from "react";
import { Filter as FilterIcon, RotateCcw } from "lucide-react";

import { Card } from "../../../shared/card/card.tsx";
import {
  Button,
  Select,
  type SelectOption,
} from "../../../ui/index.ts";
import { formatToDateOnly } from "../../../../../utils/helpers.js";
import {
  APPROVAL_CENTER_STATUS_OPTIONS,
  APPROVAL_CENTER_REGION_OPTIONS,
  DEFAULT_APPROVAL_CENTER_STATUS,
} from "../constants.ts";
import type { Phase2ListFilters } from "../hooks/use-phase2-list.ts";

import styles from "./approval-center-filters.module.css";

export type ApprovalCenterFiltersProps = {
  value: Phase2ListFilters;
  employeeOptions: SelectOption[];
  employeesLoading?: boolean;
  loading?: boolean;
  /** When true, status filter is hidden and parent `value.status` is preserved. */
  hideStatusFilter?: boolean;
  hideRegionFilter?: boolean;
  statusOptions?: SelectOption[];
  defaultStatus?: string;
  onApply: (filters: Phase2ListFilters) => void;
  onReset: () => void;
};

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function ApprovalCenterFilters({
  value,
  employeeOptions,
  employeesLoading = false,
  loading = false,
  hideStatusFilter = false,
  hideRegionFilter = false,
  statusOptions = APPROVAL_CENTER_STATUS_OPTIONS,
  defaultStatus = DEFAULT_APPROVAL_CENTER_STATUS,
  onApply,
  onReset,
}: ApprovalCenterFiltersProps) {
  const [employeeId, setEmployeeId] = useState(value.employeeId ?? "");
  const [status, setStatus] = useState(value.status ?? "");
  const [region, setRegion] = useState(value.region ?? "");
  const [startDate, setStartDate] = useState<Date | null>(parseDate(value.fromDate));
  const [endDate, setEndDate] = useState<Date | null>(parseDate(value.toDate));

  useEffect(() => {
    setEmployeeId(value.employeeId ?? "");
    setStatus(value.status ?? "");
    setRegion(value.region ?? "");
    setStartDate(parseDate(value.fromDate));
    setEndDate(parseDate(value.toDate));
  }, [value]);

  const buildFilters = (): Phase2ListFilters => ({
    employeeId: employeeId || undefined,
    status: hideStatusFilter
      ? value.status || undefined
      : status || undefined,
    region: region || undefined,
    fromDate: startDate ? formatToDateOnly(startDate) : null,
    toDate: endDate ? formatToDateOnly(endDate) : null,
  });

  const handleApply = () => onApply(buildFilters());

  const handleReset = () => {
    setEmployeeId("");
    setStatus(
      hideStatusFilter
        ? (value.status ?? defaultStatus)
        : defaultStatus,
    );
    setStartDate(null);
    setEndDate(null);
    setRegion("");
    onReset();
  };

  return (
    <Card className={styles.card} padding="3xl">
      <div className={styles.row}>
        <div className={styles.field}>
          <Select
            label="Employee Name"
            placeholder={employeesLoading ? "Loading…" : "Select Employee Name"}
            options={employeeOptions}
            value={employeeId}
            onChange={setEmployeeId}
            isClearable
            searchable
            disabled={employeesLoading}
          />
        </div>

        {!hideStatusFilter ? (
          <div className={styles.field}>
            <Select
              label="Status"
              placeholder="Select status"
              options={statusOptions}
              value={status}
              onChange={setStatus}
            />
          </div>
        ) : null}

        {!hideRegionFilter && <div className={styles.field}>
          <Select
            label="Region"
            placeholder="Select Region"
            options={APPROVAL_CENTER_REGION_OPTIONS}
            value={region}
            onChange={setRegion}
            isClearable
          />
        </div>}

        {/* <div className={styles.fieldWide}>
          <DateRange
            label="Date Range"
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            placeholderText="Select date range"
          />
        </div> */}
        <div className={styles.actions}>
          <Button
            variant="accent"
            type="button"
            onClick={handleApply}
            disabled={loading}
            startIcon={<FilterIcon size={14} aria-hidden />}
          >
            Apply Filter
          </Button>
          <Button
            variant="light-grey"
            type="button"
            onClick={handleReset}
            disabled={loading}
            startIcon={<RotateCcw size={14} aria-hidden />}
          >
            Reset
          </Button>
        </div>
      </div>


    </Card>
  );
}
